import type { AircraftObservation, AltitudeRef, Freshness, LatLon, MetarReport } from './types.ts';
import { distanceM } from './geo.ts';
import type { FreshnessThresholds } from './world.ts';

/**
 * Altitude references and position integrity (aviation data spec §3.3). Pure helpers used by the
 * public aircraft provider, the runtime and the UI.
 */

export const STANDARD_ALTIMETER_INHG = 29.92;
/** Meters of geoid ignored: EGM2008 differences inside the coarse table are larger than this. */
const M_PER_FT = 0.3048;

/** MSL ≈ pressure altitude + (altimeter − 29.92) × 1000 ft. */
export function pressureAltToMslFt(paFt: number, altimeterInHg: number): number {
  return paFt + (altimeterInHg - STANDARD_ALTIMETER_INHG) * 1000;
}

/**
 * Coarse EGM2008 geoid undulation N (m) for CONUS: inverse-distance weighting over anchor points,
 * accurate to a few metres (≈ −33 m at 36.7 N 121.6 W). Returns null outside CONUS — callers then keep the
 * height as HAE. MSL = HAE − N. Replace with a real EGM2008 grid when the reference-data lane lands.
 */
const GEOID_ANCHORS: ReadonlyArray<[number, number, number]> = [
  [36.66, -121.6, -33], [37.77, -122.42, -32], [34.05, -118.25, -35], [32.7, -117.16, -35], [38.58, -121.49, -30],
  [40.8, -124.16, -27], [47.6, -122.3, -23], [45.5, -122.7, -22], [43.6, -116.2, -15], [36.17, -115.14, -29],
  [33.45, -112.07, -29], [40.76, -111.89, -17], [39.74, -104.99, -17], [35.08, -106.65, -22], [46.87, -114.0, -16],
  [32.78, -96.8, -26], [29.76, -95.37, -27], [39.1, -94.58, -29], [44.98, -93.27, -28], [41.88, -87.63, -34],
  [33.75, -84.39, -30], [25.76, -80.19, -27], [38.9, -77.04, -33], [40.71, -74.0, -33], [42.36, -71.06, -28],
  [35.23, -80.84, -32], [30.0, -90.07, -26], [46.8, -100.78, -24],
];

export function geoidUndulationM(lat: number, lon: number): number | null {
  if (!(lat >= 24 && lat <= 50 && lon >= -125.5 && lon <= -66)) return null;
  let wSum = 0;
  let vSum = 0;
  for (const [la, lo, n] of GEOID_ANCHORS) {
    const d = distanceM({ lat, lon }, { lat: la, lon: lo });
    if (d < 1000) return n;
    const w = 1 / (d * d);
    wSum += w;
    vSum += w * n;
  }
  return Math.round((vSum / wSum) * 10) / 10;
}

/** GNSS height above the ellipsoid (ft) → MSL (ft); null when no geoid value is known here. */
export function haeToMslFt(haeFt: number, pos: LatLon): number | null {
  const n = geoidUndulationM(pos.lat, pos.lon);
  return n == null ? null : haeFt - n / M_PER_FT;
}

/** DO-260B NACp → 95% horizontal accuracy bound (m). NACp 0/unknown → null. */
export function nacpToAccuracyM(nacp: number | null | undefined): number | null {
  switch (nacp) {
    case 11: return 3;
    case 10: return 10;
    case 9: return 30;
    case 8: return 93;
    case 7: return 185;
    case 6: return 556;
    case 5: return 926;
    case 4: return 1852;
    case 3: return 3704;
    case 2: return 7408;
    case 1: return 18520;
    default: return null;
  }
}

/** Below this NACp the position reads "LOW INTEGRITY" (≥ 93 m). */
export const LOW_INTEGRITY_NACP = 8;
/** MLAT positions are widened to at least this 95% bound (tens to hundreds of metres). */
export const MLAT_MIN_ACCURACY_M = 150;

export function isLowIntegrity(o: Pick<AircraftObservation, 'nacp' | 'accuracyM'>): boolean {
  if (o.nacp != null && o.nacp < LOW_INTEGRITY_NACP) return true;
  return o.accuracyM != null && o.accuracyM >= 93;
}

/**
 * Newest METAR altimeter setting at the mission airport nearest `pos` (≤ 3 h old at `t`).
 * `airports` are the mission's airports (any objects with icao + lat/lon).
 */
export function nearestAltimeterInHg(
  pos: LatLon,
  t: number,
  metars: readonly MetarReport[],
  airports: ReadonlyArray<{ icao: string; lat: number; lon: number }>,
): { inHg: number; stationId: string } | null {
  let best: { inHg: number; stationId: string; d: number } | null = null;
  for (const m of metars) {
    if (m.altimeterInHg == null || m.t > t || t - m.t > 3 * 3600_000) continue;
    const ap = airports.find((a) => a.icao.toUpperCase() === m.stationId.toUpperCase());
    if (!ap) continue;
    const d = distanceM(pos, ap);
    if (!best || d < best.d) best = { inHg: m.altimeterInHg, stationId: m.stationId, d };
  }
  return best ? { inHg: best.inHg, stationId: best.stationId } : null;
}

/**
 * Normalise a provider observation's altitude to MSL where possible (aviation data spec §3.3):
 * 'PA' + a nearby altimeter → MSL ('baro+altimeter'); 'HAE' + a geoid value → MSL ('geo-geoid').
 * Otherwise the reference is kept and labelled. Returns a new object.
 */
export function normalizeObservationAltitude<O extends Pick<AircraftObservation, 'lat' | 'lon' | 'altFt' | 'altRef' | 'altMethod'>>(
  o: O,
  altimeterInHg: number | null,
): O {
  if (o.altFt == null) return o;
  if (o.altRef === 'PA') {
    if (altimeterInHg == null) return o;
    return { ...o, altFt: Math.round(pressureAltToMslFt(o.altFt, altimeterInHg)), altRef: 'MSL', altMethod: 'baro+altimeter' };
  }
  if (o.altRef === 'HAE') {
    const msl = haeToMslFt(o.altFt, o);
    if (msl == null) return o;
    return { ...o, altFt: Math.round(msl), altRef: 'MSL', altMethod: 'geo-geoid' };
  }
  return o;
}

/** "MSL" / "PRESS ALT" / "HAE" label for an altitude reference (absent = MSL). */
export function altitudeRefLabel(ref: AltitudeRef | null | undefined): string {
  return ref === 'PA' ? 'PRESS ALT' : ref === 'HAE' ? 'HAE' : 'MSL';
}

// ───────────── per-source staleness (aviation data spec §2) ─────────────

/** Local ADS-B receiver (readsb/dump1090 on the LAN): stale at 3 s, dropped at 30 s. */
export const LOCAL_ADSB_FRESHNESS: FreshnessThresholds = { live: 2, aging: 3, stale: 30 };

/** Source tag prefixes that use the local-receiver thresholds. */
export const LOCAL_ADSB_SOURCE_PREFIX = 'adsb.local';

/** Crew-reported fallback positions: tagged `reported:CALLSIGN`, never shown as live. */
export const REPORTED_SOURCE_PREFIX = 'reported:';

export function isReportedSource(source: string | null | undefined): boolean {
  return !!source && source.startsWith(REPORTED_SOURCE_PREFIX);
}

/** Freshness never better than 'stale' (for reported / planned values). */
export function atMostStale(f: Freshness): Freshness {
  return f === 'live' || f === 'aging' ? 'stale' : f;
}
